import { useId } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';
import { FormField } from '@/components/app';
import { PromoCodeDialogProps } from '../types';
import { fromTashkentInput, toTashkentInput } from '@/lib/datetime';

export function PromoCodeDialog({
  isOpen,
  onOpenChange,
  formData,
  onFormDataChange,
  onSubmit,
  title,
  description,
  submitLabel,
  isSubmitting = false,
  errors = {},
  onClearFieldError,
}: PromoCodeDialogProps) {
  const ids = useId().replace(/:/g, '');

  /** datetime-local (время Ташкента) → ISO; пусто → null; неверная дата — тост и без изменений. */
  const onDateChange = (field: 'valid_from' | 'valid_until', value: string) => {
    if (value) {
      const iso = fromTashkentInput(value);
      if (!iso) {
        toast.error('Неверный формат даты');
        return;
      }
      onFormDataChange((prev) => ({ ...prev, [field]: iso }));
    } else {
      onFormDataChange((prev) => ({ ...prev, [field]: null }));
    }
    if (field === 'valid_until') onClearFieldError?.('valid_until');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] max-w-2xl gap-0 overflow-y-auto p-0">
        <DialogHeader bordered>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <FormField label="Код" required error={errors.code} hint={errors.code ? undefined : 'Сохраняется заглавными буквами'}>
              <Input
                value={formData.code}
                onChange={(e) => {
                  onFormDataChange((prev) => ({ ...prev, code: e.target.value.toUpperCase() }));
                  onClearFieldError?.('code');
                }}
                placeholder="SALE50"
                className="font-mono text-[13px]"
              />
            </FormField>
            <FormField label="Тип" required>
              {(p) => (
                <Select
                  value={formData.type}
                  onValueChange={(value: 'percent' | 'fixed') => onFormDataChange((prev) => ({ ...prev, type: value }))}
                >
                  <SelectTrigger {...p}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Процент</SelectItem>
                    <SelectItem value="fixed">Фикс. сумма</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <FormField
              label="Значение"
              required
              error={errors.value}
              hint={errors.value ? undefined : formData.type === 'percent' ? 'От 1 до 100%' : 'Сумма скидки в сумах'}
            >
              <Input
                type="number"
                value={formData.value}
                onChange={(e) => {
                  onFormDataChange((prev) => ({ ...prev, value: parseInt(e.target.value) || 0 }));
                  onClearFieldError?.('value');
                }}
                min={1}
                max={formData.type === 'percent' ? 100 : undefined}
                className="tabular-nums"
              />
            </FormField>
            <FormField label="Макс. использований" hint="Пусто — без лимита">
              <Input
                type="number"
                value={formData.max_uses || ''}
                onChange={(e) =>
                  onFormDataChange((prev) => ({ ...prev, max_uses: e.target.value ? parseInt(e.target.value) : null }))
                }
                placeholder="Без лимита"
                className="tabular-nums"
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
            <FormField label="Действует с" hint="Время Ташкента">
              <Input
                type="datetime-local"
                value={toTashkentInput(formData.valid_from)}
                onChange={(e) => onDateChange('valid_from', e.target.value)}
              />
            </FormField>
            <FormField label="Действует до" error={errors.valid_until} hint="Время Ташкента">
              <Input
                type="datetime-local"
                value={toTashkentInput(formData.valid_until)}
                onChange={(e) => onDateChange('valid_until', e.target.value)}
              />
            </FormField>
          </div>
          <div className="flex items-center justify-between gap-3 rounded-xl border border-border-soft bg-surface-alt px-3 py-2.5">
            <span className="flex flex-col gap-0.5">
              <span id={`${ids}-active`} className="text-sm font-semibold">
                Активен
              </span>
              <span className="text-xs text-text-2">Код можно ввести при оплате</span>
            </span>
            <Switch
              aria-labelledby={`${ids}-active`}
              checked={formData.is_active}
              onCheckedChange={(checked) => onFormDataChange((prev) => ({ ...prev, is_active: checked }))}
            />
          </div>
        </DialogBody>
        <DialogFooter bordered>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Отмена
          </Button>
          <Button onClick={() => void onSubmit()} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="animate-spin" />}
            {submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
